/**
 * Sample MockCoins usage for the signed-in screen.
 * Replace `snapshotFor` with the usage API later. Keep this shape.
 */

export type CoinRangeId = 'month' | 'last-month' | '7d';
export type CoinGrain = 'daily' | 'weekly' | 'monthly';
export type CoinModule = 'stationary' | 'playlist' | 'itinerary' | 'joystick' | 'other';
export type CoinMap = 'new-york' | 'delhi' | 'route' | 'joystick' | 'none';
export type CoinSort = 'newest' | 'oldest';
export type CoinModuleFilter = 'all' | CoinModule;

export interface CoinPoint {
  label: string;
  hint: string;
  coins: number;
  showLabel: boolean;
}

export interface CoinModuleSlice {
  id: CoinModule;
  label: string;
  coins: number;
  pct: number;
}

export interface CoinDetailRow {
  id: 'location' | 'started' | 'ended' | 'duration' | 'coins' | 'accuracy' | 'device' | 'note';
  label: string;
  value: string;
  hint?: string;
}

export interface CoinEntry {
  id: string;
  title: string;
  module: CoinModule;
  moduleLabel: string;
  subtitle: string;
  time: string;
  coins: number;
  sortAt: string;
  mode: string;
  when: string;
  summary: string;
  map: CoinMap;
  mapLabel: string;
  rows: CoinDetailRow[];
}

export interface CoinDay {
  id: string;
  label: string;
  sortAt: string;
  entries: CoinEntry[];
}

export interface CoinInsight {
  id: string;
  tone: 'green' | 'purple' | 'pink';
  title: string;
  body: string;
}

export interface CoinHighlight {
  label: string;
  module: CoinModule;
  coins: number;
  pct: number;
}

export interface CoinSnapshot {
  id: CoinRangeId;
  rangeLabel: string;
  balance: number;
  totalUsed: number;
  totalDelta: number;
  totalDeltaText: string;
  avgDaily: number;
  avgDelta: number;
  avgDeltaText: string;
  mostUsed: CoinHighlight;
  topActivity: { label: string; coins: number; pct: number };
  modules: CoinModuleSlice[];
  series: Record<CoinGrain, CoinPoint[]>;
  highlight: Record<CoinGrain, string>;
  days: CoinDay[];
  insights: CoinInsight[];
  empty?: boolean;
}

export const COIN_RANGES: Array<{ id: CoinRangeId; label: string }> = [
  { id: 'month', label: 'This Month' },
  { id: 'last-month', label: 'Last Month' },
  { id: '7d', label: 'Last 7 days' },
];

const MODULE_LABEL: Record<CoinModule, string> = {
  stationary: 'Stationary',
  playlist: 'Playlist',
  itinerary: 'Itinerary',
  joystick: 'Joystick',
  other: 'Settings',
};

const SEPTEMBER = [10, 11, 9, 13, 15, 17, 14, 10, 13, 18, 35, 68, 21, 17, 12, 11, 16, 19, 12, 10, 8, 7, 11, 8, 9, 7, 6, 4, 4, 5];

const AUGUST = [14, 18, 12, 22, 16, 20, 32, 15, 19, 28, 17, 21, 38, 18, 16, 25, 22, 14, 19, 27, 16, 18, 23, 20, 15, 26, 21, 17, 13, 24, 12];

export function formatCoins(value: number): string {
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function dailyPoints(values: number[], month: string): CoinPoint[] {
  return values.map((coins, index) => {
    const day = index + 1;
    const hint = `${day} ${month}`;
    const showLabel = day === 1 || day % 5 === 0 || day === values.length;
    return {
      label: showLabel ? hint : '',
      hint,
      coins,
      showLabel,
    };
  });
}

function weeklyPoints(values: number[], month: string): CoinPoint[] {
  const points: CoinPoint[] = [];
  for (let index = 0; index < values.length; index += 7) {
    const slice = values.slice(index, index + 7);
    const start = index + 1;
    const end = index + slice.length;
    const hint = start === end ? `${start} ${month}` : `${start}\u2013${end} ${month}`;
    const coins = slice.reduce((sum, value) => sum + value, 0);
    points.push({ label: hint, hint, coins, showLabel: true });
  }
  return points;
}

function monthPoint(label: string, coins: number): CoinPoint {
  return { label, hint: `${label} 2026`, coins, showLabel: true };
}

interface EntryInput {
  id: string;
  title: string;
  module: CoinModule;
  subtitle: string;
  time: string;
  coins: number;
  sortAt: string;
  mode: string;
  when: string;
  summary: string;
  map: CoinMap;
  mapLabel: string;
  location: string;
  coords?: string;
  started: string;
  ended: string;
  duration: string;
  accuracy: string;
  note?: string;
}

function entry(input: EntryInput): CoinEntry {
  return {
    id: input.id,
    title: input.title,
    module: input.module,
    moduleLabel: MODULE_LABEL[input.module],
    subtitle: input.subtitle,
    time: input.time,
    coins: input.coins,
    sortAt: input.sortAt,
    mode: input.mode,
    when: input.when,
    summary: input.summary,
    map: input.map,
    mapLabel: input.mapLabel,
    rows: [
      { id: 'location', label: 'Location', value: input.location, hint: input.coords },
      { id: 'started', label: 'Started at', value: input.started },
      { id: 'ended', label: 'Ended at', value: input.ended },
      { id: 'duration', label: 'Duration', value: input.duration },
      { id: 'coins', label: 'Coins used', value: `${input.coins} coins` },
      { id: 'accuracy', label: 'Accuracy', value: input.accuracy },
      { id: 'device', label: 'Device', value: 'Samsung Galaxy F42' },
      { id: 'note', label: 'Note', value: input.note ?? '\u2014' },
    ],
  };
}

function day(id: string, label: string, sortAt: string, entries: CoinEntry[]): CoinDay {
  return { id, label, sortAt, entries };
}

const SEPTEMBER_DAYS: CoinDay[] = [
  day('2026-09-12', 'Today · 12 Sep 2026', '2026-09-12', [
    entry({
      id: 'ny-start',
      title: 'Started Mock Location',
      module: 'stationary',
      subtitle: 'Stationary · New York, USA',
      time: '10:24 PM',
      coins: 5,
      sortAt: '2026-09-12T22:24:00',
      mode: 'Stationary Mode',
      when: '12 Sep 2026 · 10:24 PM',
      summary: 'You started a stationary mock location session at the selected location.',
      map: 'new-york',
      mapLabel: 'Map of New York',
      location: 'New York, USA',
      coords: '40.7128° N, 74.0060° W',
      started: '12 Sep 2026, 10:24 PM',
      ended: '12 Sep 2026, 11:10 PM',
      duration: '46 minutes',
      accuracy: '10 meters',
    }),
    entry({
      id: 'joy-15',
      title: 'Used Joystick (15 mins)',
      module: 'joystick',
      subtitle: 'Joystick · Duration: 15 mins',
      time: '09:48 PM',
      coins: 5,
      sortAt: '2026-09-12T21:48:00',
      mode: 'Joystick',
      when: '12 Sep 2026 · 09:48 PM',
      summary: 'You moved the mock location with the joystick for 15 minutes.',
      map: 'joystick',
      mapLabel: 'Map of the joystick path',
      location: 'New York, USA',
      coords: 'On the move',
      started: '12 Sep 2026, 09:48 PM',
      ended: '12 Sep 2026, 10:03 PM',
      duration: '15 minutes',
      accuracy: '15 meters',
    }),
    entry({
      id: 'office-route',
      title: 'Played Office Route',
      module: 'playlist',
      subtitle: 'Playlist · 12 locations',
      time: '07:12 PM',
      coins: 12,
      sortAt: '2026-09-12T19:12:00',
      mode: 'Playlist',
      when: '12 Sep 2026 · 07:12 PM',
      summary: 'You played a saved playlist and visited each mock location in order.',
      map: 'route',
      mapLabel: 'Map of the saved route',
      location: 'Office Route',
      coords: '12 locations',
      started: '12 Sep 2026, 07:12 PM',
      ended: '12 Sep 2026, 07:48 PM',
      duration: '36 minutes',
      accuracy: '10 meters',
    }),
    entry({
      id: 'itinerary-drive',
      title: 'Generated Itinerary',
      module: 'itinerary',
      subtitle: 'Itinerary · 8 stops · Driving',
      time: '04:06 PM',
      coins: 20,
      sortAt: '2026-09-12T16:06:00',
      mode: 'Itinerary',
      when: '12 Sep 2026 · 04:06 PM',
      summary: 'You generated a driving itinerary with 8 stops.',
      map: 'route',
      mapLabel: 'Map of the saved route',
      location: 'Driving itinerary',
      coords: '8 stops',
      started: '12 Sep 2026, 04:06 PM',
      ended: '12 Sep 2026, 05:02 PM',
      duration: '56 minutes',
      accuracy: '10 meters',
    }),
    entry({
      id: 'accuracy-high',
      title: 'Changed Accuracy Settings',
      module: 'other',
      subtitle: 'Settings · High accuracy (5m)',
      time: '12:32 PM',
      coins: 2,
      sortAt: '2026-09-12T12:32:00',
      mode: 'Settings',
      when: '12 Sep 2026 · 12:32 PM',
      summary: 'You switched the mock session to high accuracy.',
      map: 'none',
      mapLabel: 'No map for this activity',
      location: 'Account settings',
      started: '12 Sep 2026, 12:32 PM',
      ended: '12 Sep 2026, 12:32 PM',
      duration: '1 minute',
      accuracy: '5 meters',
    }),
    entry({
      id: 'airport-run',
      title: 'Played Airport Run',
      module: 'playlist',
      subtitle: 'Playlist · 6 locations',
      time: '11:40 AM',
      coins: 8,
      sortAt: '2026-09-12T11:40:00',
      mode: 'Playlist',
      when: '12 Sep 2026 · 11:40 AM',
      summary: 'You played a short playlist of 6 airport locations.',
      map: 'route',
      mapLabel: 'Map of the saved route',
      location: 'Airport Run',
      coords: '6 locations',
      started: '12 Sep 2026, 11:40 AM',
      ended: '12 Sep 2026, 12:05 PM',
      duration: '25 minutes',
      accuracy: '10 meters',
    }),
    entry({
      id: 'brooklyn-start',
      title: 'Started Mock Location',
      module: 'stationary',
      subtitle: 'Stationary · Brooklyn, USA',
      time: '10:15 AM',
      coins: 5,
      sortAt: '2026-09-12T10:15:00',
      mode: 'Stationary Mode',
      when: '12 Sep 2026 · 10:15 AM',
      summary: 'You started a stationary mock location session in Brooklyn.',
      map: 'new-york',
      mapLabel: 'Map of New York',
      location: 'Brooklyn, USA',
      coords: '40.6782° N, 73.9442° W',
      started: '12 Sep 2026, 10:15 AM',
      ended: '12 Sep 2026, 10:40 AM',
      duration: '25 minutes',
      accuracy: '10 meters',
    }),
    entry({
      id: 'joy-20',
      title: 'Used Joystick (20 mins)',
      module: 'joystick',
      subtitle: 'Joystick · Duration: 20 mins',
      time: '09:05 AM',
      coins: 11,
      sortAt: '2026-09-12T09:05:00',
      mode: 'Joystick',
      when: '12 Sep 2026 · 09:05 AM',
      summary: 'You moved the mock location with the joystick for 20 minutes.',
      map: 'joystick',
      mapLabel: 'Map of the joystick path',
      location: 'New York, USA',
      coords: 'On the move',
      started: '12 Sep 2026, 09:05 AM',
      ended: '12 Sep 2026, 09:25 AM',
      duration: '20 minutes',
      accuracy: '15 meters',
    }),
  ]),
  day('2026-09-11', 'Yesterday · 11 Sep 2026', '2026-09-11', [
    entry({
      id: 'playlist-create',
      title: 'Created New Playlist',
      module: 'playlist',
      subtitle: 'Playlist · 5 locations',
      time: '10:18 PM',
      coins: 8,
      sortAt: '2026-09-11T22:18:00',
      mode: 'Playlist',
      when: '11 Sep 2026 · 10:18 PM',
      summary: 'You saved a new playlist with 5 locations.',
      map: 'route',
      mapLabel: 'Map of the saved route',
      location: 'New playlist',
      coords: '5 locations',
      started: '11 Sep 2026, 10:18 PM',
      ended: '11 Sep 2026, 10:26 PM',
      duration: '8 minutes',
      accuracy: '10 meters',
    }),
    entry({
      id: 'delhi-start',
      title: 'Started Mock Location',
      module: 'stationary',
      subtitle: 'Stationary · Delhi, India',
      time: '06:32 PM',
      coins: 5,
      sortAt: '2026-09-11T18:32:00',
      mode: 'Stationary Mode',
      when: '11 Sep 2026 · 06:32 PM',
      summary: 'You started a stationary mock location session in Delhi.',
      map: 'delhi',
      mapLabel: 'Map of Delhi',
      location: 'Delhi, India',
      coords: '28.6139° N, 77.2090° E',
      started: '11 Sep 2026, 06:32 PM',
      ended: '11 Sep 2026, 07:05 PM',
      duration: '33 minutes',
      accuracy: '10 meters',
    }),
    entry({
      id: 'joy-30',
      title: 'Used Joystick (30 mins)',
      module: 'joystick',
      subtitle: 'Joystick · Duration: 30 mins',
      time: '11:20 AM',
      coins: 10,
      sortAt: '2026-09-11T11:20:00',
      mode: 'Joystick',
      when: '11 Sep 2026 · 11:20 AM',
      summary: 'You moved the mock location with the joystick for 30 minutes.',
      map: 'joystick',
      mapLabel: 'Map of the joystick path',
      location: 'Delhi, India',
      coords: 'On the move',
      started: '11 Sep 2026, 11:20 AM',
      ended: '11 Sep 2026, 11:50 AM',
      duration: '30 minutes',
      accuracy: '15 meters',
    }),
    entry({
      id: 'market-loop',
      title: 'Played Market Loop',
      module: 'playlist',
      subtitle: 'Playlist · 4 locations',
      time: '09:40 AM',
      coins: 4,
      sortAt: '2026-09-11T09:40:00',
      mode: 'Playlist',
      when: '11 Sep 2026 · 09:40 AM',
      summary: 'You played a short loop of 4 market locations.',
      map: 'route',
      mapLabel: 'Map of the saved route',
      location: 'Market Loop',
      coords: '4 locations',
      started: '11 Sep 2026, 09:40 AM',
      ended: '11 Sep 2026, 09:58 AM',
      duration: '18 minutes',
      accuracy: '10 meters',
    }),
    entry({
      id: 'itinerary-walk',
      title: 'Generated Itinerary',
      module: 'itinerary',
      subtitle: 'Itinerary · 3 stops · Walking',
      time: '08:12 AM',
      coins: 6,
      sortAt: '2026-09-11T08:12:00',
      mode: 'Itinerary',
      when: '11 Sep 2026 · 08:12 AM',
      summary: 'You generated a walking itinerary with 3 stops.',
      map: 'route',
      mapLabel: 'Map of the saved route',
      location: 'Walking itinerary',
      coords: '3 stops',
      started: '11 Sep 2026, 08:12 AM',
      ended: '11 Sep 2026, 08:40 AM',
      duration: '28 minutes',
      accuracy: '10 meters',
    }),
    entry({
      id: 'accuracy-balanced',
      title: 'Changed Accuracy Settings',
      module: 'other',
      subtitle: 'Settings · Balanced (20m)',
      time: '07:05 AM',
      coins: 2,
      sortAt: '2026-09-11T07:05:00',
      mode: 'Settings',
      when: '11 Sep 2026 · 07:05 AM',
      summary: 'You switched the mock session to balanced accuracy.',
      map: 'none',
      mapLabel: 'No map for this activity',
      location: 'Account settings',
      started: '11 Sep 2026, 07:05 AM',
      ended: '11 Sep 2026, 07:05 AM',
      duration: '1 minute',
      accuracy: '20 meters',
    }),
  ]),
  day('2026-09-10', '10 Sep 2026', '2026-09-10', [
    entry({
      id: 'harbor-loop',
      title: 'Played Harbor Loop',
      module: 'playlist',
      subtitle: 'Playlist · 4 locations',
      time: '08:40 PM',
      coins: 10,
      sortAt: '2026-09-10T20:40:00',
      mode: 'Playlist',
      when: '10 Sep 2026 · 08:40 PM',
      summary: 'You played a harbor playlist with 4 locations.',
      map: 'route',
      mapLabel: 'Map of the saved route',
      location: 'Harbor Loop',
      coords: '4 locations',
      started: '10 Sep 2026, 08:40 PM',
      ended: '10 Sep 2026, 09:05 PM',
      duration: '25 minutes',
      accuracy: '10 meters',
    }),
    entry({
      id: 'jersey-start',
      title: 'Started Mock Location',
      module: 'stationary',
      subtitle: 'Stationary · Jersey City, USA',
      time: '03:12 PM',
      coins: 5,
      sortAt: '2026-09-10T15:12:00',
      mode: 'Stationary Mode',
      when: '10 Sep 2026 · 03:12 PM',
      summary: 'You started a stationary mock location session in Jersey City.',
      map: 'new-york',
      mapLabel: 'Map of New York',
      location: 'Jersey City, USA',
      coords: '40.7178° N, 74.0431° W',
      started: '10 Sep 2026, 03:12 PM',
      ended: '10 Sep 2026, 03:36 PM',
      duration: '24 minutes',
      accuracy: '10 meters',
    }),
    entry({
      id: 'joy-10',
      title: 'Used Joystick (10 mins)',
      module: 'joystick',
      subtitle: 'Joystick · Duration: 10 mins',
      time: '11:05 AM',
      coins: 3,
      sortAt: '2026-09-10T11:05:00',
      mode: 'Joystick',
      when: '10 Sep 2026 · 11:05 AM',
      summary: 'You moved the mock location with the joystick for 10 minutes.',
      map: 'joystick',
      mapLabel: 'Map of the joystick path',
      location: 'Jersey City, USA',
      coords: 'On the move',
      started: '10 Sep 2026, 11:05 AM',
      ended: '10 Sep 2026, 11:15 AM',
      duration: '10 minutes',
      accuracy: '15 meters',
    }),
  ]),
  day('2026-09-08', '8 Sep 2026', '2026-09-08', [
    entry({
      id: 'hoboken-start',
      title: 'Started Mock Location',
      module: 'stationary',
      subtitle: 'Stationary · Hoboken, USA',
      time: '07:15 PM',
      coins: 5,
      sortAt: '2026-09-08T19:15:00',
      mode: 'Stationary Mode',
      when: '8 Sep 2026 · 07:15 PM',
      summary: 'You started a stationary mock location session in Hoboken.',
      map: 'new-york',
      mapLabel: 'Map of New York',
      location: 'Hoboken, USA',
      coords: '40.7440° N, 74.0324° W',
      started: '8 Sep 2026, 07:15 PM',
      ended: '8 Sep 2026, 07:40 PM',
      duration: '25 minutes',
      accuracy: '10 meters',
    }),
    entry({
      id: 'accuracy-battery',
      title: 'Changed Accuracy Settings',
      module: 'other',
      subtitle: 'Settings · Battery saving',
      time: '02:40 PM',
      coins: 2,
      sortAt: '2026-09-08T14:40:00',
      mode: 'Settings',
      when: '8 Sep 2026 · 02:40 PM',
      summary: 'You switched the mock session to battery saving accuracy.',
      map: 'none',
      mapLabel: 'No map for this activity',
      location: 'Account settings',
      started: '8 Sep 2026, 02:40 PM',
      ended: '8 Sep 2026, 02:41 PM',
      duration: '1 minute',
      accuracy: '50 meters',
    }),
    entry({
      id: 'joy-8',
      title: 'Used Joystick (8 mins)',
      module: 'joystick',
      subtitle: 'Joystick · Duration: 8 mins',
      time: '09:30 AM',
      coins: 3,
      sortAt: '2026-09-08T09:30:00',
      mode: 'Joystick',
      when: '8 Sep 2026 · 09:30 AM',
      summary: 'You moved the mock location with the joystick for 8 minutes.',
      map: 'joystick',
      mapLabel: 'Map of the joystick path',
      location: 'Hoboken, USA',
      coords: 'On the move',
      started: '8 Sep 2026, 09:30 AM',
      ended: '8 Sep 2026, 09:38 AM',
      duration: '8 minutes',
      accuracy: '15 meters',
    }),
  ]),
];

const AUGUST_DAYS: CoinDay[] = [
  day('2026-08-28', '28 Aug 2026', '2026-08-28', [
    entry({
      id: 'aug-office',
      title: 'Played Office Route',
      module: 'playlist',
      subtitle: 'Playlist · 12 locations',
      time: '09:12 PM',
      coins: 16,
      sortAt: '2026-08-28T21:12:00',
      mode: 'Playlist',
      when: '28 Aug 2026 · 09:12 PM',
      summary: 'You played the office playlist across 12 locations.',
      map: 'route',
      mapLabel: 'Map of the saved route',
      location: 'Office Route',
      coords: '12 locations',
      started: '28 Aug 2026, 09:12 PM',
      ended: '28 Aug 2026, 09:50 PM',
      duration: '38 minutes',
      accuracy: '10 meters',
    }),
    entry({
      id: 'aug-chicago',
      title: 'Started Mock Location',
      module: 'stationary',
      subtitle: 'Stationary · Chicago, USA',
      time: '06:40 PM',
      coins: 5,
      sortAt: '2026-08-28T18:40:00',
      mode: 'Stationary Mode',
      when: '28 Aug 2026 · 06:40 PM',
      summary: 'You started a stationary mock location session in Chicago.',
      map: 'route',
      mapLabel: 'Map of the saved route',
      location: 'Chicago, USA',
      coords: '41.8781° N, 87.6298° W',
      started: '28 Aug 2026, 06:40 PM',
      ended: '28 Aug 2026, 07:10 PM',
      duration: '30 minutes',
      accuracy: '10 meters',
    }),
    entry({
      id: 'aug-drive',
      title: 'Generated Itinerary',
      module: 'itinerary',
      subtitle: 'Itinerary · 6 stops · Driving',
      time: '02:15 PM',
      coins: 18,
      sortAt: '2026-08-28T14:15:00',
      mode: 'Itinerary',
      when: '28 Aug 2026 · 02:15 PM',
      summary: 'You generated a driving itinerary with 6 stops.',
      map: 'route',
      mapLabel: 'Map of the saved route',
      location: 'Chicago drive',
      coords: '6 stops',
      started: '28 Aug 2026, 02:15 PM',
      ended: '28 Aug 2026, 03:05 PM',
      duration: '50 minutes',
      accuracy: '10 meters',
    }),
    entry({
      id: 'aug-joy',
      title: 'Used Joystick (25 mins)',
      module: 'joystick',
      subtitle: 'Joystick · Duration: 25 mins',
      time: '11:02 AM',
      coins: 8,
      sortAt: '2026-08-28T11:02:00',
      mode: 'Joystick',
      when: '28 Aug 2026 · 11:02 AM',
      summary: 'You moved the mock location with the joystick for 25 minutes.',
      map: 'joystick',
      mapLabel: 'Map of the joystick path',
      location: 'Chicago, USA',
      coords: 'On the move',
      started: '28 Aug 2026, 11:02 AM',
      ended: '28 Aug 2026, 11:27 AM',
      duration: '25 minutes',
      accuracy: '15 meters',
    }),
  ]),
  day('2026-08-21', '21 Aug 2026', '2026-08-21', [
    entry({
      id: 'aug-playlist',
      title: 'Created New Playlist',
      module: 'playlist',
      subtitle: 'Playlist · 7 locations',
      time: '08:20 PM',
      coins: 8,
      sortAt: '2026-08-21T20:20:00',
      mode: 'Playlist',
      when: '21 Aug 2026 · 08:20 PM',
      summary: 'You saved a new playlist with 7 locations.',
      map: 'route',
      mapLabel: 'Map of the saved route',
      location: 'August playlist',
      coords: '7 locations',
      started: '21 Aug 2026, 08:20 PM',
      ended: '21 Aug 2026, 08:31 PM',
      duration: '11 minutes',
      accuracy: '10 meters',
    }),
    entry({
      id: 'aug-start',
      title: 'Started Mock Location',
      module: 'stationary',
      subtitle: 'Stationary · Austin, USA',
      time: '04:05 PM',
      coins: 5,
      sortAt: '2026-08-21T16:05:00',
      mode: 'Stationary Mode',
      when: '21 Aug 2026 · 04:05 PM',
      summary: 'You started a stationary mock location session in Austin.',
      map: 'route',
      mapLabel: 'Map of the saved route',
      location: 'Austin, USA',
      coords: '30.2672° N, 97.7431° W',
      started: '21 Aug 2026, 04:05 PM',
      ended: '21 Aug 2026, 04:28 PM',
      duration: '23 minutes',
      accuracy: '10 meters',
    }),
    entry({
      id: 'aug-accuracy',
      title: 'Changed Accuracy Settings',
      module: 'other',
      subtitle: 'Settings · High accuracy (5m)',
      time: '10:18 AM',
      coins: 2,
      sortAt: '2026-08-21T10:18:00',
      mode: 'Settings',
      when: '21 Aug 2026 · 10:18 AM',
      summary: 'You switched the mock session to high accuracy.',
      map: 'none',
      mapLabel: 'No map for this activity',
      location: 'Account settings',
      started: '21 Aug 2026, 10:18 AM',
      ended: '21 Aug 2026, 10:18 AM',
      duration: '1 minute',
      accuracy: '5 meters',
    }),
  ]),
];

const THIS_MONTH_INSIGHTS: CoinInsight[] = [
  {
    id: 'lower',
    tone: 'green',
    title: '32% lower usage',
    body: 'You used 32% fewer coins than last month.',
  },
  {
    id: 'weekend',
    tone: 'purple',
    title: 'Weekend usage is higher',
    body: 'You typically use more coins on weekends.',
  },
  {
    id: 'save',
    tone: 'pink',
    title: 'Save coins',
    body: 'Use Playlists or Stationary mode for longer sessions.',
  },
];

function buildMonth(): CoinSnapshot {
  return {
    id: 'month',
    rangeLabel: 'This Month',
    balance: 1580,
    totalUsed: 420,
    totalDelta: -32,
    totalDeltaText: 'less than last month',
    avgDaily: 14,
    avgDelta: -18,
    avgDeltaText: 'less than last month',
    mostUsed: { label: 'Stationary', module: 'stationary', coins: 176, pct: 42 },
    topActivity: { label: 'Started Mock Location', coins: 120, pct: 28 },
    modules: [
      { id: 'stationary', label: 'Stationary', coins: 176, pct: 42 },
      { id: 'playlist', label: 'Playlist', coins: 118, pct: 28 },
      { id: 'itinerary', label: 'Itinerary', coins: 76, pct: 18 },
      { id: 'joystick', label: 'Joystick', coins: 34, pct: 8 },
      { id: 'other', label: 'Other / Settings', coins: 16, pct: 4 },
    ],
    series: {
      daily: dailyPoints(SEPTEMBER, 'Sep'),
      weekly: weeklyPoints(SEPTEMBER, 'Sep'),
      monthly: [
        monthPoint('Apr', 280),
        monthPoint('May', 360),
        monthPoint('Jun', 410),
        monthPoint('Jul', 390),
        monthPoint('Aug', 618),
        monthPoint('Sep', 420),
      ],
    },
    highlight: { daily: '12 Sep', weekly: '8\u201314 Sep', monthly: 'Sep 2026' },
    days: SEPTEMBER_DAYS,
    insights: THIS_MONTH_INSIGHTS,
  };
}

function buildLastMonth(): CoinSnapshot {
  return {
    id: 'last-month',
    rangeLabel: 'Last Month',
    balance: 1580,
    totalUsed: 618,
    totalDelta: 12,
    totalDeltaText: 'more than July',
    avgDaily: 20,
    avgDelta: 8,
    avgDeltaText: 'more than July',
    mostUsed: { label: 'Playlist', module: 'playlist', coins: 247, pct: 40 },
    topActivity: { label: 'Played Office Route', coins: 160, pct: 26 },
    modules: [
      { id: 'playlist', label: 'Playlist', coins: 247, pct: 40 },
      { id: 'stationary', label: 'Stationary', coins: 173, pct: 28 },
      { id: 'itinerary', label: 'Itinerary', coins: 111, pct: 18 },
      { id: 'joystick', label: 'Joystick', coins: 49, pct: 8 },
      { id: 'other', label: 'Other / Settings', coins: 38, pct: 6 },
    ],
    series: {
      daily: dailyPoints(AUGUST, 'Aug'),
      weekly: weeklyPoints(AUGUST, 'Aug'),
      monthly: [
        monthPoint('Mar', 300),
        monthPoint('Apr', 280),
        monthPoint('May', 360),
        monthPoint('Jun', 410),
        monthPoint('Jul', 552),
        monthPoint('Aug', 618),
      ],
    },
    highlight: { daily: '13 Aug', weekly: '8\u201314 Aug', monthly: 'Aug 2026' },
    days: AUGUST_DAYS,
    insights: [
      {
        id: 'playlist-led',
        tone: 'purple',
        title: 'Playlist led the month',
        body: 'Playlists spent 247 coins, more than any other module.',
      },
      {
        id: 'routes',
        tone: 'green',
        title: 'Routes cost more',
        body: 'Generated itineraries cost more per session than a stationary point.',
      },
      {
        id: 'reuse',
        tone: 'pink',
        title: 'Reuse a playlist',
        body: 'Playing a saved playlist spends fewer coins than generating a new route.',
      },
    ],
  };
}

function buildWeek(): CoinSnapshot {
  const daily = dailyPoints(SEPTEMBER, 'Sep')
    .slice(5, 12)
    .map((point) => ({ ...point, label: point.hint, showLabel: true }));
  const days = SEPTEMBER_DAYS.filter((item) => item.sortAt >= '2026-09-06' && item.sortAt <= '2026-09-12');
  return {
    id: '7d',
    rangeLabel: 'Last 7 days',
    balance: 1580,
    totalUsed: 175,
    totalDelta: -10,
    totalDeltaText: 'less than the previous 7 days',
    avgDaily: 25,
    avgDelta: -6,
    avgDeltaText: 'less than the previous 7 days',
    mostUsed: { label: 'Stationary', module: 'stationary', coins: 74, pct: 42 },
    topActivity: { label: 'Started Mock Location', coins: 40, pct: 23 },
    modules: [
      { id: 'stationary', label: 'Stationary', coins: 74, pct: 42 },
      { id: 'playlist', label: 'Playlist', coins: 49, pct: 28 },
      { id: 'itinerary', label: 'Itinerary', coins: 28, pct: 16 },
      { id: 'joystick', label: 'Joystick', coins: 17, pct: 10 },
      { id: 'other', label: 'Other / Settings', coins: 7, pct: 4 },
    ],
    series: {
      daily,
      weekly: [{ label: '6\u201312 Sep', hint: '6\u201312 Sep', coins: 175, showLabel: true }],
      monthly: [
        monthPoint('Apr', 280),
        monthPoint('May', 360),
        monthPoint('Jun', 410),
        monthPoint('Jul', 390),
        monthPoint('Aug', 618),
        { label: 'Sep', hint: 'Sep 2026', coins: 175, showLabel: true },
      ],
    },
    highlight: { daily: '12 Sep', weekly: '6\u201312 Sep', monthly: 'Sep 2026' },
    days,
    insights: [
      {
        id: 'peak',
        tone: 'green',
        title: 'Peak on 12 Sep',
        body: 'That day spent 68 coins, mostly on a long itinerary.',
      },
      {
        id: 'still-stationary',
        tone: 'purple',
        title: 'Stationary still leads',
        body: 'Fixed locations remain the module you open most.',
      },
      {
        id: 'short',
        tone: 'pink',
        title: 'Short sessions cost less',
        body: 'A stationary start is 5 coins. A generated route costs more.',
      },
    ],
  };
}

const BUILDERS: Record<CoinRangeId, () => CoinSnapshot> = {
  month: buildMonth,
  'last-month': buildLastMonth,
  '7d': buildWeek,
};

export function snapshotFor(id: CoinRangeId): CoinSnapshot {
  return BUILDERS[id]();
}

export interface CoinDayFilter {
  module: CoinModuleFilter;
  query: string;
  sort: CoinSort;
}

export function filterCoinDays(days: CoinDay[], filter: CoinDayFilter): CoinDay[] {
  const query = filter.query.trim().toLowerCase();
  const sign = filter.sort === 'newest' ? -1 : 1;
  return days
    .map((item) => ({
      ...item,
      entries: item.entries.filter((row) => {
        if (filter.module !== 'all' && row.module !== filter.module) return false;
        if (!query) return true;
        const haystack = `${row.title} ${row.subtitle} ${row.moduleLabel} ${row.summary}`.toLowerCase();
        return haystack.includes(query);
      }),
    }))
    .filter((item) => item.entries.length > 0)
    .sort((a, b) => a.sortAt.localeCompare(b.sortAt) * sign)
    .map((item) => ({
      ...item,
      entries: [...item.entries].sort((a, b) => a.sortAt.localeCompare(b.sortAt) * sign),
    }));
}
